/**
 * A long press on a touch screen, as the map's right-click.
 *
 * iOS Safari never fires `contextmenu`, so on an iPhone or iPad the map's
 * command menu (GOTO here, orbit here, copy coordinates) simply did not exist.
 * This watches the touch stream and reports a press held still for `delayMs`.
 * Android Chrome does fire `contextmenu` on a long press, so the caller asks
 * `firedRecently()` to drop that second, duplicate open.
 *
 * Plain functions over plain points, so the timing is testable without a map.
 */

export interface PressPoint {
  x: number;
  y: number;
}

export interface LongPressOptions {
  onLongPress: (point: PressPoint) => void;
  /** iOS's own long-press is about 500ms; matching it feels native. */
  delayMs?: number;
  /** A finger drifts a few pixels while held. Beyond this it is a pan. */
  tolerancePx?: number;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export interface LongPress {
  /** `touches` is how many fingers are down; only a single finger counts. */
  start: (point: PressPoint, touches: number) => void;
  move: (point: PressPoint, touches: number) => void;
  end: () => void;
  /** True while the finger that fired is down and briefly after it lifts: the
   *  contextmenu or click that follows is the same gesture, not a new one. */
  firedRecently: () => boolean;
}

export function createLongPress({
  onLongPress,
  delayMs = 500,
  tolerancePx = 10,
  now = () => Date.now(),
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
}: LongPressOptions): LongPress {
  let timer: unknown = null;
  let origin: PressPoint | null = null;
  /** The finger that fired is still down. */
  let holding = false;
  let quietUntil = -Infinity;

  const cancel = () => {
    if (timer !== null) clearTimer(timer);
    timer = null;
    origin = null;
  };

  return {
    start: (point, touches) => {
      cancel();
      holding = false;
      // Two fingers is a pinch or a rotate, never a press.
      if (touches !== 1) return;
      origin = point;
      timer = setTimer(() => {
        timer = null;
        const at = origin;
        origin = null;
        if (!at) return;
        holding = true;
        onLongPress(at);
      }, delayMs);
    },
    move: (point, touches) => {
      if (!origin) return;
      if (touches !== 1 || Math.hypot(point.x - origin.x, point.y - origin.y) > tolerancePx) cancel();
    },
    end: () => {
      cancel();
      // The click a browser may synthesise from the lift lands just after it.
      if (holding) quietUntil = now() + 400;
      holding = false;
    },
    firedRecently: () => holding || now() < quietUntil,
  };
}
